/**
 * Canonical exposure-control identities (specification sections 3.1 and 3.2), computed in the
 * browser so a route can be read from the API and committed on chain by its `routeId`.
 *
 * A `routeId` commits to the backing domain, both endpoints and the operation, so an identity
 * built from a wrong input (for example a CEP-18 contract hash where the package hash belongs)
 * is a route no contract registers: the API answers `EXPOSURE_ROUTE_UNKNOWN` and the contract
 * reverts. It can never name another route. Pinned against core's
 * `fixtures/exposure-id-vectors.json` (src/test/exposureRouteIdentity.test.ts).
 */
import {
  encodeAbiParameters,
  keccak256,
  pad,
  stringToHex,
  toHex,
  type Hex,
} from "viem";

import type { TokenOriginChain } from "types";
import type { ExposureOperation } from "./contract/exposure";

export const CHAIN_KIND = { EVM: 1, CASPER: 2 } as const;
export const ADDRESS_KIND = {
  EVM: 1,
  CASPER_ACCOUNT: 2,
  CASPER_PACKAGE: 3,
} as const;
export const OPERATION: Record<ExposureOperation, number> = {
  LOCK_MINT: 1,
  BURN_UNLOCK: 2,
  LOCK_MINT_WRAPPED: 3,
  BURN_WRAPPED_UNLOCK: 4,
};

const tag = (s: string): Hex => keccak256(stringToHex(s));
export const DOMAIN_TAG = tag("ASTRALBEAM_BACKING_DOMAIN_V1");
export const ROUTE_TAG = tag("ASTRALBEAM_ROUTE_V1");
export const PROTOCOL_ID = tag("ASTRALBEAM_MAINNET_BRIDGE");

const BYTES32 = /^0x[0-9a-f]{64}$/;

/** A canonical 32-byte word: lowercase `0x` + 64 hex. */
function word(value: Hex): Hex {
  const lower = value.toLowerCase() as Hex;
  if (!BYTES32.test(lower)) throw new Error(`not a 32-byte word: ${value}`);
  return lower;
}

export interface Endpoint {
  chainKind: number;
  chainId: Hex;
  bridgeKind: number;
  bridgeValue: Hex;
}

export interface DomainOrigin {
  chainKind: number;
  chainId: Hex;
  custodyKind: number;
  custodyValue: Hex;
  assetKind: number;
  assetValue: Hex;
}

export function backingDomainId(d: DomainOrigin): Hex {
  return keccak256(
    encodeAbiParameters(
      [
        { type: "bytes32" },
        { type: "bytes32" },
        { type: "uint8" },
        { type: "bytes32" },
        { type: "uint8" },
        { type: "bytes32" },
        { type: "uint8" },
        { type: "bytes32" },
      ],
      [
        DOMAIN_TAG,
        PROTOCOL_ID,
        d.chainKind,
        word(d.chainId),
        d.custodyKind,
        word(d.custodyValue),
        d.assetKind,
        word(d.assetValue),
      ],
    ),
  );
}

export function routeId(
  domain: Hex,
  source: Endpoint,
  destination: Endpoint,
  operationKind: number,
): Hex {
  return keccak256(
    encodeAbiParameters(
      [
        { type: "bytes32" },
        { type: "bytes32" },
        { type: "uint8" },
        { type: "bytes32" },
        { type: "uint8" },
        { type: "bytes32" },
        { type: "uint8" },
        { type: "bytes32" },
        { type: "uint8" },
        { type: "bytes32" },
        { type: "uint8" },
      ],
      [
        ROUTE_TAG,
        word(domain),
        source.chainKind,
        word(source.chainId),
        source.bridgeKind,
        word(source.bridgeValue),
        destination.chainKind,
        word(destination.chainId),
        destination.bridgeKind,
        word(destination.bridgeValue),
        operationKind,
      ],
    ),
  );
}

/** An EVM chain value: its `uint256` chain id as a big-endian word. */
export const evmChainWord = (chainId: number | bigint): Hex => {
  const id = BigInt(chainId);
  if (id <= 0n) throw new Error(`invalid EVM chain id: ${chainId}`);
  return pad(toHex(id), { size: 32 });
};

/** A Casper chain value: `keccak256` of the exact, case-sensitive network name. */
export const casperChainWord = (networkName: string): Hex => {
  if (!networkName) throw new Error("Casper network name is required");
  return keccak256(stringToHex(networkName));
};

/** A left-zero-padded 20-byte EVM address. */
export const evmAddressWord = (address: string): Hex => {
  if (!/^0x[0-9a-fA-F]{40}$/.test(address))
    throw new Error(`not an EVM address: ${address}`);
  return pad(address.toLowerCase() as Hex, { size: 32 });
};

/** A Casper package hash (`hash-` + 64 hex, or bare / `0x` 64 hex) as a word. */
export const casperHashWord = (hash: string): Hex => {
  const hex = hash
    .replace(/^hash-/, "")
    .replace(/^0x/i, "")
    .toLowerCase();
  if (!/^[0-9a-f]{64}$/.test(hex))
    throw new Error(`not a 32-byte Casper hash: ${hash}`);
  return `0x${hex}`;
};

export type BeamDirection = "evm_to_casper" | "casper_to_evm";

/**
 * The operation of a beam: an EVM-origin asset locks out of and unlocks back into its EVM
 * custody; a Casper-origin asset does the same at `TokenFactory` (specification section 3.2).
 */
export function operationFor(
  origin: TokenOriginChain,
  direction: BeamDirection,
): ExposureOperation {
  if (origin === "evm")
    return direction === "evm_to_casper" ? "LOCK_MINT" : "BURN_UNLOCK";
  return direction === "casper_to_evm"
    ? "LOCK_MINT_WRAPPED"
    : "BURN_WRAPPED_UNLOCK";
}

export interface RouteInputs {
  origin: TokenOriginChain;
  direction: BeamDirection;
  evmChainId: number;
  /** The chain's locker proxy: EVM custody and EVM bridge endpoint. */
  evmLocker: string;
  /** Casper chainspec name, e.g. `casper-test`. */
  casperNetwork: string;
  /** `TokenFactory` package hash: Casper custody and Casper bridge endpoint. */
  tokenFactoryPackage: string;
  /** The origin asset: ERC-20 address for an EVM-origin asset, CEP-18 package hash for a Casper-origin one. */
  originAsset: string;
}

export interface RouteIdentity {
  backingDomainId: Hex;
  routeId: Hex;
  operationKind: ExposureOperation;
}

export function routeIdentity(r: RouteInputs): RouteIdentity {
  const evm: Endpoint = {
    chainKind: CHAIN_KIND.EVM,
    chainId: evmChainWord(r.evmChainId),
    bridgeKind: ADDRESS_KIND.EVM,
    bridgeValue: evmAddressWord(r.evmLocker),
  };
  const casper: Endpoint = {
    chainKind: CHAIN_KIND.CASPER,
    chainId: casperChainWord(r.casperNetwork),
    bridgeKind: ADDRESS_KIND.CASPER_PACKAGE,
    bridgeValue: casperHashWord(r.tokenFactoryPackage),
  };
  const domain: DomainOrigin =
    r.origin === "evm"
      ? {
          chainKind: CHAIN_KIND.EVM,
          chainId: evm.chainId,
          custodyKind: ADDRESS_KIND.EVM,
          custodyValue: evm.bridgeValue,
          assetKind: ADDRESS_KIND.EVM,
          assetValue: evmAddressWord(r.originAsset),
        }
      : {
          chainKind: CHAIN_KIND.CASPER,
          chainId: casper.chainId,
          custodyKind: ADDRESS_KIND.CASPER_PACKAGE,
          custodyValue: casper.bridgeValue,
          assetKind: ADDRESS_KIND.CASPER_PACKAGE,
          assetValue: casperHashWord(r.originAsset),
        };
  const operationKind = operationFor(r.origin, r.direction);
  const domainId = backingDomainId(domain);
  const [source, destination] =
    r.direction === "evm_to_casper" ? [evm, casper] : [casper, evm];

  return {
    backingDomainId: domainId,
    routeId: routeId(domainId, source, destination, OPERATION[operationKind]),
    operationKind,
  };
}
